import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BalanceIcon from '@mui/icons-material/Balance';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalculateIcon from '@mui/icons-material/Calculate';
import ComputerIcon from '@mui/icons-material/Computer';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import GavelIcon from '@mui/icons-material/Gavel';
import InsightsIcon from '@mui/icons-material/Insights';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PeopleIcon from '@mui/icons-material/People';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import type { SvgIconComponent } from '@mui/icons-material';
import type { Course } from '../services/courseService';

// Normalize external system values (Docebo / CPAX) → NASBA-approved field names.
// Keep in sync with FieldOfStudyNormalizer.cs (course sync) and
// CpeHistoryController.FosNameAliases (CPAx credit sync).
export const CATEGORY_MAP: Record<string, string> = {
  'Tax':                                  'Taxes',
  'Taxation':                             'Taxes',
  'Technology':                           'Information Technology',
  'Business Management':                  'Business Management and Organization',
  'Business Management & Organization':   'Business Management and Organization',
  'Ethics':                               'Regulatory Ethics',
  'Governmental Accounting':              'Accounting (Governmental)',
  'Governmental Auditing':                'Auditing (Governmental)',
  'Management':                           'Management Services',
  'HR':                                   'Personnel/Human Resources',
  'Human Resources':                      'Personnel/Human Resources',
  'Computer Software':                    'Computer Software & Applications',
  'Software':                             'Computer Software & Applications',
  'Communications':                       'Communications and Marketing',
  'Marketing':                            'Communications and Marketing',
  'Specialized':                          'Specialized Knowledge',
};

export function normalizeCategory(category: string): string {
  const trimmed = category.trim();
  return CATEGORY_MAP[trimmed] ?? trimmed;
}

// Keys must be canonical (post-normalization) names so that getCategoryIcon works correctly.
export const CATEGORY_ICON_MAP: Record<string, SvgIconComponent> = {
  'Taxes':                          AccountBalanceIcon,
  'Regulatory Ethics':              BalanceIcon,
  'Behavioral Ethics':              BalanceIcon,
  'Information Technology':         ComputerIcon,
  'Computer Software & Applications': ComputerIcon,
  'Computer Science':               ComputerIcon,
  'Business Management and Organization': CorporateFareIcon,
  'Business Law':                   GavelIcon,
  'Accounting':                     CalculateIcon,
  'Accounting (Governmental)':      CalculateIcon,
  'Auditing':                       FindInPageIcon,
  'Auditing (Governmental)':        FindInPageIcon,
  'Finance':                        TrendingUpIcon,
  'Management Services':            CorporateFareIcon,
  'Personnel/Human Resources':      PeopleIcon,
  'Personal Development':           SchoolIcon,
  'Specialized Knowledge':          MenuBookIcon,
  'Statistics':                     BarChartIcon,
  'Economics':                      InsightsIcon,
  'Production':                     PrecisionManufacturingIcon,
  'Other':                          MenuBookIcon,
};

export function getCategoryIcon(category: string): SvgIconComponent {
  return CATEGORY_ICON_MAP[normalizeCategory(category)] ?? MenuBookIcon;
}

// Fields of study come from course credits (canonical NASBA names linked to
// education_categories). Falls back to the normalized raw category only for
// courses whose credits haven't been synced yet.
export function getFieldsOfStudy(course: Course): string[] {
  const fromCredits = [...new Set((course.courseCredits ?? []).map(c => c.educationCategoryName).filter(Boolean))];
  if (fromCredits.length > 0) return fromCredits;
  const fallback = normalizeCategory(course.category ?? '');
  return fallback ? [fallback] : [];
}
